import { Box, Card, CardContent, Stack, Typography } from '@mui/material'
import { FunctionComponent } from 'react'
import { IconPicker } from '../../display-elements-components/icon-picker'
import { IconName } from '../../../../shared/interfaces/icon-picker.model'

/**
 * SelectableCard
 * -------------------------------------------------------------------------
 * The one clickable "choose an option" card used across the import wizard
 * (data type, emission type, upload method, supplier import action). Keeps
 * the border weight, radius, selected/hover states and icon treatment
 * identical everywhere so the steps read as a single flow. Use `align='left'`
 * for icon-beside-text cards with a description, `align='center'` for the
 * icon-on-top toggles. Add `compact` for a plain one-line icon+label choice
 * (no description): it drops the tall minimum height and tightens the padding,
 * so a list of them reads as a set of choices rather than a wall of panels.
 *
 * Vertical alignment: with `fullHeight`, a card stretches to the tallest in its
 * row. A card with a description keeps its content at the top so the titles line
 * up across the row; a card WITHOUT one centres its icon+title in the space
 * instead, because there is nothing below to align to and a top-hugged label in
 * a tall card just looks unfinished.
 */
export const SelectableCard: FunctionComponent<{
  title: string
  description?: string
  icon?: IconName
  selected?: boolean
  disabled?: boolean
  onClick: () => void
  align?: 'center' | 'left'
  minHeight?: number
  fullHeight?: boolean
  compact?: boolean
}> = ({
  title,
  description,
  icon,
  selected = false,
  disabled = false,
  onClick,
  align = 'center',
  minHeight,
  fullHeight = false,
  compact = false,
}) => {
  const isCenter = align === 'center'
  const resolvedMinHeight = minHeight ?? (compact ? 0 : 100)
  // Nothing below the title to align to, so the icon+title centre in whatever height the row gives us.
  const centreVertically = isCenter || !description
  return (
    <Card
      onClick={() => { if (!disabled) onClick() }}
      sx={{
        cursor: disabled ? 'default' : 'pointer',
        height: fullHeight ? '100%' : 'auto',
        // Card is a flex column so CardContent can fill it via flexGrow. Do NOT go back to
        // `height: '100%'` on CardContent: MuiCardContent is box-sizing:content-box, so 100% means
        // "100% of the card PLUS my own 48px of padding". Measured on the real component, that made
        // a 224px content box inside a 180px card -- top-anchored, centring itself within its own
        // oversized box (so the content sat visibly low) and clipped at the bottom by MuiCard's
        // overflow:hidden. flexGrow has no such trap.
        ...(fullHeight && { display: 'flex', flexDirection: 'column' }),
        boxShadow: 'none',
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: selected ? 'primary.main' : 'lightGrey.main',
        borderRadius: '10px',
        backgroundColor: selected ? 'secondary.main' : 'common.white',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
        '&:hover': {
          borderColor: 'primary.main',
          backgroundColor: selected ? 'secondary.light' : 'action.hover',
        },
      }}
    >
      <CardContent
        sx={{
          // Grow to fill the flex Card above. Never a percentage height — see the note there.
          ...(fullHeight ? { flexGrow: 1 } : { height: 'auto' }),
          p: compact ? 2 : 3,
          display: 'flex',
          flexDirection: isCenter ? 'column' : 'row',
          // Column: cross-axis is horizontal. Row: cross-axis is vertical, so this is what actually
          // centres an icon+title pair that has no description under it.
          alignItems: isCenter ? 'center' : (centreVertically ? 'center' : 'flex-start'),
          justifyContent: isCenter ? 'center' : 'flex-start',
          gap: isCenter ? 0 : 2,
          minHeight: resolvedMinHeight,
          '&:last-child': { pb: compact ? 2 : 3 },
        }}
      >
        {icon && (
          <Box sx={{
            color: 'text.secondary',
            mb: isCenter ? 1 : 0,
            // The nudge exists to optically align the icon with the first line of a title that has
            // copy beneath it. With no description the pair is centred, so the nudge just skews it.
            pt: (isCenter || centreVertically) ? 0 : 0.5,
            flex: 'none',
          }}>
            <IconPicker icon={icon} />
          </Box>
        )}
        <Stack sx={{ flexGrow: isCenter ? 0 : 1, minWidth: 0, alignItems: isCenter ? 'center' : 'flex-start' }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 600, color: 'text.primary', textAlign: isCenter ? 'center' : 'left' }}>
            {title}
          </Typography>
          {description && (
            <Typography variant='body2' sx={{ color: 'text.secondary', textAlign: isCenter ? 'center' : 'left', mt: 0.5 }}>
              {description}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}
